import { useMemo } from "preact/hooks";
import {
  CheckIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { computeHunks, type Hunk } from "../lib/diff-hunks";
import { Badge, Button } from "./ui";

export type EditProposalTurn = {
  id: string;
  role: "assistant";
  mode: "edit";
  proposedBody: string;
  streaming: boolean;
  status: "pending" | "applied" | "discarded";
};

type Props = {
  turn: EditProposalTurn;
  currentBody: string;
  onApply: () => void;
  onDiscard: () => void;
};

export function EditProposalCard({ turn, currentBody, onApply, onDiscard }: Props) {
  // While streaming the proposal is incomplete, so computing a diff would
  // flash misleading hunks on every flush. Defer until done.
  const hunks: Hunk[] | null = useMemo(
    () =>
      turn.streaming || !turn.proposedBody.trim()
        ? null
        : computeHunks(currentBody, turn.proposedBody, 2),
    [currentBody, turn.proposedBody, turn.streaming],
  );
  const changeCount = hunks?.length ?? 0;
  const whitespaceOnlyChange =
    hunks !== null && hunks.length === 0 && turn.proposedBody !== currentBody;

  return (
    <div class="mr-6 space-y-2 rounded-lg border border-border bg-bg px-3 py-2.5 text-sm text-fg-muted">
      <div class="flex flex-wrap items-center gap-1.5 text-xs font-medium text-fg">
        <PencilSquareIcon class="h-3.5 w-3.5 text-fg-subtle" />
        編集案
        {turn.streaming && (
          <span class="ml-1 inline-flex items-center gap-1 text-[11px] font-normal text-fg-subtle">
            <span class="inline-block h-1 w-1 animate-pulse rounded-full bg-fg-faint" />
            生成中…
          </span>
        )}
        {!turn.streaming && turn.status === "applied" && (
          <Badge tone="success">
            <CheckIcon class="h-3 w-3" />
            反映済み
          </Badge>
        )}
        {!turn.streaming && turn.status === "discarded" && (
          <Badge tone="neutral">破棄</Badge>
        )}
      </div>

      {hunks !== null && hunks.length === 0 && (
        <p class="rounded-md border border-border bg-bg-subtle/60 px-2.5 py-1.5 text-[11px] text-fg-subtle">
          {whitespaceOnlyChange
            ? "空白のみの変更が含まれています。"
            : "現在の本文との差分はありません。"}
        </p>
      )}

      {hunks !== null && hunks.length > 0 && (
        <details
          class="rounded-md border border-border bg-bg-subtle/40 text-xs"
          open
        >
          <summary class="cursor-pointer select-none px-2.5 py-1.5 font-medium text-fg-muted">
            変更箇所 ({changeCount} 箇所)
          </summary>
          <div class="max-h-72 overflow-auto border-t border-border bg-bg font-mono text-[11px] leading-relaxed">
            {hunks.map((h, i) => (
              <DiffHunkRow key={i} hunk={h} />
            ))}
          </div>
        </details>
      )}

      {!turn.streaming && turn.status === "pending" && (
        <div class="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="secondary"
            iconLeft={<XMarkIcon class="h-3.5 w-3.5" />}
            onClick={onDiscard}
          >
            破棄
          </Button>
          <Button
            size="sm"
            variant="primary"
            iconLeft={<CheckIcon class="h-3.5 w-3.5" />}
            onClick={onApply}
            disabled={!turn.proposedBody.trim()}
          >
            反映する
          </Button>
        </div>
      )}

      {!turn.streaming && turn.status === "pending" && !turn.proposedBody.trim() && (
        <p class="text-[11px] text-fg-subtle">
          (未反映の編集案) 内容が空のため反映できません。もう一度依頼してください。
        </p>
      )}
    </div>
  );
}

function DiffHunkRow({ hunk }: { hunk: Hunk }) {
  return (
    <div class="border-b border-border-subtle last:border-b-0">
      <div class="bg-bg-subtle px-2.5 py-1 font-mono text-[10px] text-fg-subtle">
        @@ -{hunk.oldStart} +{hunk.newStart} @@
      </div>
      {hunk.lines.map((line, i) => {
        const base = "whitespace-pre-wrap break-words px-2.5 py-0.5";
        if (line.kind === "add") {
          return (
            <div key={i} class={`${base} bg-success-bg text-success-fg`}>
              <span class="select-none opacity-70">+ </span>
              {line.text || " "}
            </div>
          );
        }
        if (line.kind === "del") {
          return (
            <div key={i} class={`${base} bg-danger-bg text-danger-fg`}>
              <span class="select-none opacity-70">- </span>
              {line.text || " "}
            </div>
          );
        }
        return (
          <div key={i} class={`${base} text-fg-muted`}>
            <span class="select-none text-fg-faint">{"  "}</span>
            {line.text || " "}
          </div>
        );
      })}
    </div>
  );
}
